import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { EventFormData } from '@/types';
import { eventApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { ImageUploadField } from '@/components/forms/ImageUploadField';
import { FreeOfferingField } from '@/components/forms/FreeOfferingField';
import { TagInput } from '@/components/ui/TagInput';
import { CategorySelectField } from '@/components/forms/CategorySelectField';
import { VideoUploadField } from '@/components/forms/VideoUploadField';

const audienceSuggestions = [
  'Kids (Age 7-12)',
  'Open to All',
  'Teens',
  'College Students',
  'Professionals',
];

function parseCommaSeparated(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

interface AdminEventFormProps {
  defaultCategory?: 'event' | 'workshop';
}

export const AdminEventForm = ({ defaultCategory = 'event' }: AdminEventFormProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [submitError, setSubmitError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<EventFormData>({
    defaultValues: {
      categoryId: '',
      category: defaultCategory,
      title: '',
      city: '',
      venue: '',
      dateText: '',
      audience: [],
      level: 'Open to All',
      teachingMode: 'Offline',
      coverImageKey: '',
      previewVideoKey: '',
      priceText: '',
      isFree: false,
      tagsText: '',
      description: '',
    },
  });
  const coverImageKey = watch('coverImageKey');
  const previewVideoKey = watch('previewVideoKey');
  const isFree = watch('isFree');
  const category = watch('category');
  const pageTitle = category === 'workshop' ? 'Workshop' : 'Event';

  useEffect(() => {
    if (!isEditMode || !id) {
      setCoverPreviewUrl('');
      setVideoPreviewUrl('');
      return;
    }

    const controller = new AbortController();

    const fetchEvent = async () => {
      try {
        const eventItem = await eventApi.getEventById(id, {
          signal: controller.signal,
          category: defaultCategory,
        });
        if (!eventItem) return;

        reset({
          categoryId: eventItem.categoryId || '',
          category: eventItem.category || defaultCategory,
          title: eventItem.title,
          city: eventItem.city,
          venue: eventItem.venue,
          dateText: eventItem.dateText,
          audience: eventItem.audience || [],
          level: eventItem.level || 'Open to All',
          teachingMode: eventItem.teachingMode || 'Offline',
          coverImageKey: eventItem.coverImageKey,
          previewVideoKey: eventItem.previewVideoKey || '',
          priceText: eventItem.priceText,
          isFree: eventItem.isFree ?? false,
          tagsText: (eventItem.tags || []).join(', '),
          description: eventItem.description || '',
        });
        setCoverPreviewUrl(eventItem.coverImageUrl || '');
        setVideoPreviewUrl(eventItem.previewVideoUrl || '');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch event:', error);
        }
      }
    };

    void fetchEvent();
    return () => controller.abort();
  }, [defaultCategory, id, isEditMode, reset]);

  const onSubmit = async (data: EventFormData) => {
    setSubmitError('');
    setIsLoading(true);

    const payload = {
      categoryId: data.categoryId,
      category: data.category,
      title: data.title,
      city: data.city,
      venue: data.venue,
      dateText: data.dateText,
      audience: data.audience,
      level: data.level,
      teachingMode: data.teachingMode,
      coverImageKey: data.coverImageKey,
      previewVideoKey: data.previewVideoKey,
      priceText: data.priceText,
      isFree: Boolean(data.isFree),
      tags: parseCommaSeparated(data.tagsText),
      description: data.description,
    };

    try {
      if (isEditMode && id) {
        await eventApi.updateEvent(id, payload);
      } else {
        await eventApi.createEvent(payload);
      }
      navigate('/admin/events');
    } catch (error) {
      setSubmitError((error as Error).message || `Failed to save ${pageTitle.toLowerCase()}.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="page-heading">{isEditMode ? `Edit ${pageTitle}` : `Create ${pageTitle}`}</h1>
        <p className="page-subtitle">
          Manage events and workshops from one unified module while keeping category-specific presentation clear.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="form-label">Listing Type</label>
              <select className="form-control min-h-11" {...register('category', { required: 'Category is required' })}>
                <option value="event">Event</option>
                <option value="workshop">Workshop</option>
              </select>
            </div>

            <Controller
              control={control}
              name="categoryId"
              rules={{ required: 'Please select a category' }}
              render={({ field }) => (
                <CategorySelectField
                  type="event"
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={errors.categoryId?.message}
                />
              )}
            />

            <Input
              label={`${pageTitle} Title`}
              placeholder={category === 'workshop' ? 'Weekend Design Workshop' : 'Community Learning Event'}
              {...register('title', { required: 'Title is required' })}
              error={errors.title?.message}
            />

            <Input
              label="City"
              placeholder="e.g., Chennai"
              {...register('city', { required: 'City is required' })}
              error={errors.city?.message}
            />

            <Input
              label="Venue"
              placeholder="e.g., Marina Grounds"
              {...register('venue', { required: 'Venue is required' })}
              error={errors.venue?.message}
            />

            <Input
              label="Date and Time"
              placeholder="Sat, 10:00 AM - 1:00 PM"
              {...register('dateText', { required: 'Date and time text is required' })}
              error={errors.dateText?.message}
            />

            <div>
              <label className="form-label">Level</label>
              <Input
                placeholder="Open to All"
                {...register('level', { required: 'Level is required' })}
                error={errors.level?.message}
              />
            </div>

            <div>
              <label className="form-label">Teaching Mode</label>
              <select className="form-control min-h-11" {...register('teachingMode', { required: true })}>
                <option value="Offline">Offline</option>
                <option value="Online">Online</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <Controller
                control={control}
                name="audience"
                rules={{ validate: (value) => value.length > 0 || 'Add at least one audience segment' }}
                render={({ field }) => (
                  <TagInput
                    label="Audience"
                    values={field.value || []}
                    onChange={field.onChange}
                    suggestions={audienceSuggestions}
                    placeholder="Add audience groups"
                    error={errors.audience?.message}
                    helperText="This appears on cards, detail pages, and filters to make the event easier to scan."
                  />
                )}
              />
            </div>

            <input type="hidden" {...register('coverImageKey', { required: `${pageTitle} cover image is required` })} />
            <div className="md:col-span-2">
              <ImageUploadField
                label="Cover Image"
                folder="banners"
                imageKey={coverImageKey || ''}
                previewUrl={coverPreviewUrl}
                error={errors.coverImageKey?.message}
                helperText="Use a strong landscape image that still reads well on card previews."
                onChange={({ key, url }) => {
                  setValue('coverImageKey', key, { shouldValidate: true, shouldDirty: true });
                  setCoverPreviewUrl(url);
                }}
              />
            </div>

            <input type="hidden" {...register('previewVideoKey')} />
            <div className="md:col-span-2">
              <VideoUploadField
                folder="event-videos"
                videoKey={previewVideoKey || ''}
                previewUrl={videoPreviewUrl}
                onChange={({ key, url }) => {
                  setValue('previewVideoKey', key, { shouldDirty: true });
                  setVideoPreviewUrl(url);
                }}
              />
            </div>

            <Input
              label="Price Text"
              placeholder="e.g., Rs 499"
              {...register('priceText', { required: 'Price text is required' })}
              error={errors.priceText?.message}
            />

            <div className="md:col-span-2">
              <FreeOfferingField
                checked={Boolean(isFree)}
                onChange={(checked) => setValue('isFree', checked, { shouldDirty: true })}
                itemLabel={category === 'workshop' ? 'workshop' : 'event'}
              />
            </div>

            <Input label="Tags" placeholder="Offline, Networking, Beginner friendly" {...register('tagsText')} />

            <div className="md:col-span-2">
              <label className="form-label">Description</label>
              <textarea
                className="form-control min-h-[140px]"
                placeholder="Explain the event, workshop, agenda, value, or audience fit."
                {...register('description')}
              />
            </div>
          </div>
        </Card>

        {submitError ? <p className="status-message border-red-100 bg-red-50 text-red-700">{submitError}</p> : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/events')}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : isEditMode ? `Update ${pageTitle}` : `Create ${pageTitle}`}
          </Button>
        </div>
      </form>
    </div>
  );
};
